import { Form, Input } from 'antd';
import React, { useState } from 'react';

import {
  Gap,
  Gutter,
  Icon,
  SubmitButton,
  Text,
  Title,
} from '@common/components';
import { gql, useMutation } from '@common/hooks';
import {
  AuthTemplateCentralizedContainer,
  AuthTemplateLogo,
} from '@identity/components';
import { Colors } from '@common/tokens';

const REQUEST_FORGOT_PASSWORD_MUTATION = gql`
  mutation RequestWorksUserForgotPassword(
    $input: RequestWorksUserForgotPasswordInput!
  ) {
    requestWorksUserForgotPassword(input: $input) {
      success
    }
  }
`;

export function ForgotPasswordPage() {
  const [complete, setComplete] = useState(false);
  const [email, setEmail] = useState<string | undefined>();
  const [form] = Form.useForm();

  const [requestWorksUserForgotPassword] = useMutation(
    REQUEST_FORGOT_PASSWORD_MUTATION
  );

  const onFinish = (values: { email: string | undefined }) => {
    requestWorksUserForgotPassword({
      variables: {
        input: {
          email: values.email,
        },
      },
    })
      .then(() => {
        form.resetFields();
        setComplete(true);
        setEmail(values.email);
      })
      .catch(() => {
        form.setFields([
          {
            name: 'email',
            errors: ['There was a problem with your request, try again.'],
          },
        ]);
      });
  };

  return (
    <AuthTemplateCentralizedContainer>
      <div className="forgot-password-page">
        <AuthTemplateLogo />

        <Gutter internal size="xxl" />

        <Gap vertical size="md">
          <Title size="h1">Forgot your password?</Title>

          <Form form={form} onFinish={onFinish} style={{ width: '100%' }}>
            <Form.Item
              name="email"
              rules={[
                {
                  required: true,
                  message: 'Please enter your email address',
                },
                {
                  type: 'email',
                  message: 'Please enter a valid email address',
                },
              ]}
            >
              <Input
                prefix={<Icon icon="envelope" kind="light" />}
                placeholder="email address"
                size="large"
              />
            </Form.Item>
            <Form.Item>
              <Gap vertical size="md">
                {!complete && (
                  <Text>Enter your email address to reset password.</Text>
                )}
                {complete && (
                  <>
                    <Text>
                      Instructions to reset your password has been emailed to{' '}
                      {email && email.toLowerCase()}.
                    </Text>
                    <Text>
                      Didn’t get an email? Check the spam folder or give it a
                      minute. If you still haven’t received it, try entering
                      your email above again or contact our Support Specialist
                      at{' '}
                      <a href="mailto:support@workshealth.com">
                        support@workshealth.com
                      </a>
                      .
                    </Text>
                  </>
                )}
                <SubmitButton block>Send Reset Password Link</SubmitButton>
              </Gap>
            </Form.Item>
          </Form>
        </Gap>

        <style jsx>{`
          .forgot-password-page :global(.ant-input-prefix) {
            color: ${Colors.steelDarker};
          }
          .forgot-password-page :global(input) {
            padding-left: 6px;
          }

          .forgot-password-page :global(button) {
            width: auto;
          }
        `}</style>
      </div>
    </AuthTemplateCentralizedContainer>
  );
}
